import {
  ButtonUI,
  CircleCloseButtonUI,
  LayoutPartUI,
  ScrollableBoxUI,
} from '@next-order/next-order-components';
import LoadingButton from 'components/LoadingButton';
import RequestInvoice from 'components/RequestInvoice';
import { MailIcon, PeopleIcon } from 'nexticons/outline';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import dineinImg from '../../assets/images/dinein.png';

const DineInOrderTraking = ({ data, statusText, orderTracker }) => {
  const { storeConfig } = useSelector((state) => state.layoutReducer);
  const [showSendInvoice, setShowSendInvoice] = useState(false);
  const { origin } = window.location;
  const {
    floorId = '',
    floorLayoutId = '',
    tableNumber = '',
  } = orderTracker?.orderObj || {};
  const qrOrderingLink = `/dine-in/${
    floorId || floorLayoutId
  }/table-${tableNumber}`;
  const smsUrl = `sms:?&body=Join me and place a contactless order for our table at ${storeConfig?.name}. Just click the link below and jump right in with me:%0a${origin}${qrOrderingLink}`;

  return (
    <>
      {showSendInvoice && (
        <RequestInvoice
          hideModal={() => {
            setShowSendInvoice(false);
          }}
          orderId={orderTracker?.orderObj._id}
        />
      )}
      <div className='w-full z-[99999] h-full absolute top-0 left-0 flex flex-col'>
        <div
          className='h-full w-full bg-cover bg-no-repeat max-h-35vh'
          style={{
            backgroundImage: `url(${dineinImg})`,
          }}
        >
          <div className='w-full h-full bg-black opacity-25 absolute top-0 left-0' />
          <LayoutPartUI.Row>
            <CircleCloseButtonUI
              size='2xl'
              extendedClassName='outline-0 z-[999999] bg-white m-2'
              onClick={() => {
                // window.location.href = '/?qrTracking=close';
                window.location.href = `${qrOrderingLink}?qrRedirect=true`;
              }}
            />
          </LayoutPartUI.Row>
          <div className='h-full w-full max-h-30vh flex flex-col items-center justify-center absolute top-0 z-[99999]'>
            <div className='logoWrap'>
              <img src={storeConfig.logo} alt='logo' className='logo' />
            </div>
            <h3 className='mt-0.5 text-2xl text-white text-center tracking-tight font-medium leading-8 mb-2'>
              {statusText}
            </h3>
            <h4 className='text-base text-white text-center'>
              It will be brought to{' '}
              <b> Table {orderTracker?.orderObj?.tableNumber} </b> shortly.
            </h4>
          </div>
        </div>
        <div className='h-full w-full absolute bottom-0 max-h-70vh z-[99999]'>
          <LayoutPartUI extendedClassName='relative overflow-hidden rounded-t-6.75 h-full w-full bg-white min-h-auto-container'>
            {Boolean(!data?.total || !orderTracker?.orderObj) && (
              <LoadingButton
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  right: 0,
                  bottom: 0,
                  height: '100%',
                  zIndex: 1000,
                  backgroundColor: 'rgba(230, 230, 230, 0.4)',
                }}
              />
            )}
            <LayoutPartUI.Header
              paddingX='lg'
              extendedClassName='pt-6 pb-5 -2xs:px-5 rounded-t-6.75'
            >
              <div style={{ '--color-primary': '49 113 222' }}>
                <ButtonUI
                  fullWidth
                  size='xl'
                  rounded='xl'
                  variant='outlined'
                  leftIcon={
                    <PeopleIcon
                      width={28}
                      strokeWidth={1.5}
                      className='-ml-3 mr-2'
                    />
                  }
                  onClick={() => {
                    window.open(smsUrl);
                  }}
                  extendedClassName='2xs:text-4.75 2xs:min-h-13 !border-0 shadow-cw outline-0 mb-6'
                >
                  Invite Guests to Order
                </ButtonUI>
              </div>
              <LayoutPartUI.Row
                extendedClassName='-mt-1 -2xs:flex-wrap-reverse'
                bottomMargin='lg'
                align='center'
                justify='between'
              >
                <h3 className='mt-0.5 text-2xl text-primary-text tracking-tight font-medium leading-8'>
                  My Order
                </h3>
                <ButtonUI
                  size='custom'
                  color='secondary'
                  variant='outlined'
                  fontWeight='normal'
                  extendedClassName='-mr-1.5 pl-3 min-h-8.25 grow-0 shrink-0 border-none text-3.875 !tracking-normal shadow-cw outline-0'
                  extendedInnerClassName=''
                  rounded='full'
                  leftIcon={<MailIcon width={19} className='mr-0.5' />}
                  onClick={() => {
                    setShowSendInvoice(true);
                  }}
                >
                  Request Invoice
                </ButtonUI>
              </LayoutPartUI.Row>
            </LayoutPartUI.Header>
            <LayoutPartUI.Body paddingX='none'>
              <ScrollableBoxUI
                isBottomShadow
                paddingX='xl'
                extendedClassName='pt-2 pb-5 -2xs:px-5 scroll-shadow'
              >
                {data?.items?.map(
                  ({ name, size, count, price, extras, remove }, idx) => (
                    <div
                      key={`Item-${name}-${idx}`}
                      className='leading-6 text-gray-900 mb-6.5 text-3.75 2xs:text-4.125 gap-0'
                    >
                      <div className='flex gap-5 items-center justify-center w-full'>
                        <span className='inline-flex items-center justify-center w-6.5 h-6.5 rounded-full text-primary-text font-medium bg-gray-100 min-w-[26px]'>
                          {count}
                        </span>
                        <h4 className='grow capitalize'>
                          {name}{' '}
                          {size && (
                            <span className='text-secondary'> - {size}</span>
                          )}
                        </h4>
                        <span className='text-right'>{price}</span>
                      </div>
                      {extras?.map((extraItem, idx) => (
                        <div className='pl-45p' key={`Extra-${name}-${idx}`}>
                          <span>
                            <strong>
                              Extras
                              {extraItem?.name ? `(${extraItem?.name})` : ''}:
                            </strong>{' '}
                            {extraItem?.items?.join(', ')}
                          </span>
                        </div>
                      ))}
                      {remove?.map((removeItem, idx) => (
                        <div className='pl-45p' key={`Remove-${name}-${idx}`}>
                          <span>
                            <strong>
                              Remove
                              {removeItem?.name ? `(${removeItem?.name})` : ''}:
                            </strong>{' '}
                            {removeItem?.items?.join(', ')}
                          </span>
                        </div>
                      ))}
                    </div>
                  )
                )}
                {data?.notes && (
                  <LayoutPartUI.Row
                    align='center'
                    justify='between'
                    bottomMargin='2xs'
                    extendedClassName='-mt-0.5 leading-6 text-gray-900 text-3.75 2xs:text-4.125'
                  >
                    <span>Notes</span>
                    <span className='align-end'>{data.notes}</span>
                  </LayoutPartUI.Row>
                )}
                {data?.additional?.map(({ name, amount }, idx) => (
                  <LayoutPartUI.Row
                    align='center'
                    justify='between'
                    bottomMargin='2xs'
                    extendedClassName='-mt-0.5 leading-6 text-gray-900 text-3.75 2xs:text-4.125'
                    key={`Additional-${name}-${idx}`}
                  >
                    <span>{name}</span>
                    <span>{amount}</span>
                  </LayoutPartUI.Row>
                ))}
              </ScrollableBoxUI>
            </LayoutPartUI.Body>
            <LayoutPartUI.Footer
              extendedClassName='pt-5 pb-5.5 -2xs:px-5'
              paddingX='lg'
            >
              <LayoutPartUI.Row
                align='center'
                justify='between'
                bottomMargin='3xs'
                extendedClassName='-mt-0.5 font-semibold leading-6 text-gray-900 text-3.75 2xs:text-4.125'
              >
                <span>Total</span>
                <span>{data.total}</span>
              </LayoutPartUI.Row>
              <LayoutPartUI.Row extendedClassName='-mt-0.25 px-0.5'>
                <p className='text-sm leading-6 text-secondary'>
                  {data?.paymentStatusDisplay}
                  {data?.paymentBy ? ` - ${data?.paymentBy}` : ''}
                </p>
              </LayoutPartUI.Row>
              <ButtonUI
                fullWidth
                size='xl'
                rounded='xl'
                extendedClassName='2xs:min-h-12.25 2xs:text-lg outline-0'
                onClick={() => {
                  window.location.href = `${qrOrderingLink}?qrRedirect=true`;
                }}
              >
                Add more to your Order
              </ButtonUI>
            </LayoutPartUI.Footer>
          </LayoutPartUI>
        </div>
      </div>
    </>
  );
};

export default DineInOrderTraking;
